import Button from "@common/components/Button";
import Grid from '@mui/material/Grid2';
import FilterButton from "@common/components/Button/FilterButton";
import { Divider } from "@mui/material";
import React, { useEffect, useState, useContext } from "react";
import { useTranslation } from "react-i18next";
import { RoomsRealtimeContext } from "@common/components/ListDetailViews/Layout/context";
import Select from "@common/components/Select";
import MenuItem from "@common/components/MenuItem";
import { GraphType } from "@services/monitoring/types";
import { Device } from "@services/devices/types";
import { participantSortFn } from "../const";
import { isMeetingFinished } from "@common/utils/meetings";
import { MeetingFilterViews } from "../..";

type Filter<T> = { value: T; label: string }[];

type MeetingMetricFilter =
  | "Ping_Latency"
  | "Frame_Rate"
  | "Call_Rate"
  | "Packet_Loss"
  | "Jitter_Audio"
  | "Jitter_Video"
  | "Jitter_Content";

const meetingMetricFilter: Filter<MeetingMetricFilter> = [
  { label: "Ping Latency", value: "Ping_Latency" },
  { label: "Speed", value: "Frame_Rate" },
  { label: "Bitrate", value: "Call_Rate" },
  { label: "Packet Loss (%)", value: "Packet_Loss" },
  { label: "Jitter", value: "Jitter_Audio" },
];

export type MonitoringFilterType = "metric" | "device";
type DeviceFilterType = { room: string; device: string; id: Device["id"] };
type FilterBarProps = {
  onFilterChange: Function;
};

const FilterBar = (props: FilterBarProps) => {
  const { onFilterChange } = props;
  const [filter, setFilter] = useState<FilterDefinition | undefined>();
  const { t } = useTranslation();
  const context = useContext(RoomsRealtimeContext);
  const meeting = { ...context.detail?.meeting };
  const [devices, setDevices] = useState<DeviceFilterType[]>([]);

  const defaultFilter = {
    filterType: MeetingFilterViews.Metric,
    filterValue: GraphType.Ping_Latency,
  };

  const handleClickFilter = (
    filterType: MonitoringFilterType,
    filterValue: GraphType | string
  ) => {
    if (
      filterType === filter?.filterType &&
      filterValue === filter?.filterValue &&
      !isMeetingFinished(meeting)
    )
      return undefined;
    return { filterType, filterValue };
  };

  const displayClearFilter = () => Boolean(filter);
  const handleClickClear = () => {
    if (!isMeetingFinished(meeting)) {
      setFilter(undefined);
    } else {
      setFilter(defaultFilter);
    }
  };
  useEffect(() => {
    onFilterChange(filter);
  }, [filter]);

  useEffect(() => {
    if (isMeetingFinished(meeting)) {
      setFilter({
        filterType: MeetingFilterViews.Metric,
        filterValue: GraphType.Ping_Latency,
      });
    }
  }, []);

  useEffect(() => {
    const devices: DeviceFilterType[] =
      meeting?.participants?.sort(participantSortFn).map((part) => {
        return {
          room: part.name ?? "",
          device: part.device?.name ?? "",
          id: part.device?.id ?? "",
        };
      }) ?? [];
    setDevices(devices);
  }, [meeting.participants]);
  return (
    <>
      <Grid
        container
        direction="row"
        wrap="nowrap"
        sx={(theme) => ({
          paddingTop: theme.spacing(1),
          alignItems: "center",
          minWidth: "max-content",
          width: "max-content",
          gap: 1.25,
        })}
      >
        <Grid>
          <Grid
            container
            direction="row"
            wrap="nowrap"
            sx={{ width: '100%', alignItems: "center", }}
          >
            {meetingMetricFilter.map(({ label, value }) => (
              <FilterButton
                size="small"
                key={label}
                //   disabled={loading}
                // selected={filters.some(
                //   (f) => f.filterType === "metric" && f.filterValue === value
                // )}
                selected={
                  filter?.filterType === "metric" &&
                  filter?.filterValue === value
                }
                onClick={() =>
                  setFilter(
                    handleClickFilter(
                      "metric" as MonitoringFilterType,
                      value as GraphType
                    )
                  )
                }
                value={value}
              >
                {t(label)}
              </FilterButton>
            ))}
          </Grid>
        </Grid>
        {devices && (
          <>
            <Divider orientation="vertical" flexItem />
            <Grid
              sx={{
                minWidth: "250px",
              }}>
              <Select
                label={t("Device")}
                size="small"
                value={
                  filter?.filterType === "device" ? filter?.filterValue : ""
                }
                onChange={(event) =>
                  setFilter(
                    handleClickFilter("device", event.target.value as string)
                  )
                }
                sx={() => ({
                  height: "2em",
                  "& label": {
                    lineHeight: "1rem",
                    "&.MuiFormLabel-filled, &.Mui-focused": {
                      lineHeight: "inherit",
                    },
                  },
                })}
              >
                {devices.map((device) => {
                  if (device.device) {
                    return (
                      <MenuItem value={device.id} key={device.device}>
                        {device.room} / {device.device}
                      </MenuItem>
                    );
                  }
                  return;
                })}
              </Select>
            </Grid>
          </>
        )}

        {displayClearFilter() && (
          <Grid>
            <Grid
              container
              wrap="nowrap"
              sx={{ width: '100%', justifyContent: "flex-end",
                alignItems: "center", }}
            >
              <Button
                sx={{
                  color: ({ palette }) => palette.common.grey.g828282,
                }}
                onClick={handleClickClear}
                variant="text"
              >
                {t("Clear filters")}
              </Button>
            </Grid>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default FilterBar;
